import axios from "axios";
import { ChevronDown, Moon, Play, RotateCcw, Sun, Trophy, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { GameFullscreenButton } from "./GameFullscreenButton";
import { Link } from "react-router-dom";
import { toast } from "../components/ui/sonner";
import { getCachedUser } from "../utils/auth";
import { awardPoints, getLinkedTag } from "../utils/rewards";
import { dailyKey, H, mulberry32, NIGHT_RUNNERS, POWER_ITEMS, seedFromKey, skinForDay, SPRITES, W } from "./dolosnight/constants";
import { freshNight, payoutFor, rankings, stepNight } from "./dolosnight/engine";
import { connectNightRoom, lerpNight } from "./dolosnight/net";
import { renderNight } from "./dolosnight/render";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// solo: WASD + arrows both drive P1. co-op: P1 gets WASD, P2 gets arrows
const KEYMAP_P1 = { KeyW: "up", KeyS: "down", KeyA: "left", KeyD: "right" };
const KEYMAP_P2 = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right" };
const P1_ACTIONS = { KeyE: "use", Enter: "use", KeyT: "trap", KeyR: "trap", Space: "slash", KeyK: "slash" };
const P2_ACTIONS = { ShiftRight: "use", ControlRight: "trap", Enter: "slash" };

const emptyPad = () => ({ up: false, down: false, left: false, right: false, use: false, trap: false, slash: false });

const loadImg = (src) => {
  const img = new Image();
  img.src = src;
  return img;
};

export function DolosNight() {
  const canvasRef = useRef(null);
  const spritesRef = useRef(null);
  const staticRef = useRef(null);
  const darkRef = useRef(null);
  const stateRef = useRef(null);
  const phaseRef = useRef("select");
  const keysRef = useRef({ p1: emptyPad(), p2: emptyPad() });
  const countRef = useRef(4);
  const sfxRef = useRef({});
  const modeRef = useRef("solo");
  const picksRef = useRef(["dolo", "woofer"]);
  const [phase, setPhase] = useState("select");
  const [runnerCount, setRunnerCount] = useState(4);
  const [mode, setModeState] = useState("solo");
  const [picks, setPicks] = useState(["dolo", "woofer"]);
  // online co-op — role null|host|guest; status idle|waiting|ready|joined|live
  const [online, setOnlineState] = useState({ role: null, code: "", status: "idle" });
  const onlineRef = useRef(online);
  const netRef = useRef(null);
  const guestPadRef = useRef(emptyPad()); // host: latest pad streamed from the partner
  const guestPickRef = useRef(null); // host: the partner's chosen runner
  const sendPadRef = useRef(emptyPad()); // guest: this device's own pad, streamed to the host
  const snapARef = useRef(null); // guest: older snapshot { g, t }
  const snapBRef = useRef(null); // guest: newest snapshot { g, t }
  const guestFrameRef = useRef(0); // guest: local tick so canvas pulses stay alive between snaps
  const onEventRef = useRef(null); // guest: forwarded host events land in the local handler
  const [joinCode, setJoinCode] = useState("");
  const setOnline = (next) => {
    onlineRef.current = next;
    setOnlineState(next);
  };
  const setMode = (m) => {
    modeRef.current = m;
    setModeState(m);
    if (m !== "online" && onlineRef.current.role) leaveRoom();
  };
  const setPick = (slot, key) => {
    setPicks((prev) => {
      const next = [...prev];
      const other = slot === 0 ? 1 : 0;
      if (next[other] === key) next[other] = prev[slot]; // swap so both runners stay distinct
      next[slot] = key;
      picksRef.current = next;
      return next;
    });
  };
  const [results, setResults] = useState(null);
  const [payout, setPayout] = useState(0);
  const [howtoOpen, setHowtoOpen] = useState(false);
  // weekly night board — biggest hauls and escapes on the shared daily maze
  const [nightInfo, setNightInfo] = useState(null);
  const [nightName, setNightName] = useState(() => getCachedUser()?.name || "");
  const [nightLogged, setNightLogged] = useState(null);
  const [nightBusy, setNightBusy] = useState(false);
  const myRunRef = useRef({ supplies: 0, escaped: false });
  const duoRunRef = useRef(null);
  const boardFetchRef = useRef(() => {});

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    spritesRef.current = {
      dolo: { n: SPRITES.dolo.n.map(loadImg), s: SPRITES.dolo.s.map(loadImg), e: SPRITES.dolo.e.map(loadImg) },
      characters: Object.fromEntries(NIGHT_RUNNERS.filter((nr) => nr.sprite).map((nr) => [nr.key, loadImg(nr.sprite)])),
      zombies: SPRITES.zombies.map(loadImg),
      dogs: SPRITES.dogs.map(loadImg),
      walls: SPRITES.walls.map(loadImg),
      decor: SPRITES.decor.map(loadImg),
      lamp: loadImg(SPRITES.lamp),
      door: loadImg(SPRITES.door),
      vent: loadImg(SPRITES.vent),
      supply: SPRITES.supply.map(loadImg),
      medkit: loadImg(SPRITES.medkit),
      crate: loadImg(SPRITES.crate),
      lore: SPRITES.lore.map(loadImg),
      powerIcons: Object.fromEntries(Object.entries(POWER_ITEMS).map(([k, v]) => [k, loadImg(v.icon)])),
    };
    // attract-mode backdrop behind the select overlay — same daily course
    stateRef.current = freshNight(1, 4, mulberry32(seedFromKey(dailyKey())));

    // weekly night board — biggest hauls and escapes, Monday reset
    let boardAlive = true;
    const fetchBoard = () =>
      axios
        .get(`${API}/dolosnight/board`)
        .then(({ data }) => {
          if (boardAlive) setNightInfo(data);
        })
        .catch(() => {});
    boardFetchRef.current = fetchBoard;
    fetchBoard();

    const mkSfx = (src, volume) => {
      const audio = new Audio(src);
      audio.preload = "auto";
      audio.volume = volume;
      return audio;
    };
    sfxRef.current = {
      pickup: mkSfx("/assets/game-coin.wav", 0.35),
      down: mkSfx("/assets/gate-denied.wav", 0.45),
      power: mkSfx("/assets/game-jump.wav", 0.4),
      gate: mkSfx("/assets/record-siren.wav", 0.5),
      escape: mkSfx("/assets/crowd-roar.wav", 0.5),
      boom: mkSfx("/assets/crowd-roar.wav", 0.35),
      rampage: mkSfx("/assets/crowd-roar.wav", 0.6),
      trap: mkSfx("/assets/game-jump.wav", 0.3),
      heartbeat: mkSfx("/assets/heartbeat.wav", 0.4),
    };
    const playSfx = (name) => {
      const audio = sfxRef.current[name];
      if (!audio) return;
      audio.currentTime = 0;
      audio.play().catch(() => {});
    };

    const onEvent = (kind) => {
      // the online host mirrors every beat to the partner; dawn ships one last full snapshot first
      if (onlineRef.current?.role === "host" && netRef.current) {
        if (kind === "dawn" && stateRef.current) netRef.current.send({ type: "snap", g: stateRef.current });
        netRef.current.send({ type: "event", kind });
      }
      if (kind === "pickup") playSfx("pickup");
      else if (kind === "down") playSfx("down");
      else if (kind === "power" || kind === "armor") playSfx("power");
      else if (kind === "gate") playSfx("gate");
      else if (kind === "escape") playSfx("escape");
      else if (kind === "boom") playSfx("boom");
      else if (kind === "rampage") playSfx("rampage");
      else if (kind === "trap" || kind === "trapped") playSfx("trap");
      else if (kind === "charge") playSfx("heartbeat");
      else if (kind === "dawn") {
        const g = stateRef.current;
        const board = rankings(g);
        const role = onlineRef.current.role;
        const humanRunners = g.runners.filter((r) => r.human);
        const me = role === "guest" ? g.runners[1] : humanRunners[0];
        // every device banks its own runner's haul: solo/local co-op pays every local human,
        // online pays the host P1's payout on the host device and P2's on the guest device
        const pts = role === "guest"
          ? payoutFor(g.runners[1])
          : role === "host"
            ? payoutFor(humanRunners[0])
            : humanRunners.reduce((sum, r) => sum + payoutFor(r), 0);
        if (pts > 0) awardPoints("dolosnight", pts);
        myRunRef.current = { supplies: me.supplies, escaped: me.escaped };
        // duo board entry: local co-op or the online HOST logs the combined haul; the guest never logs
        duoRunRef.current = role === "guest"
          ? null
          : modeRef.current === "coop" || role === "host"
            ? {
                duo: `${NIGHT_RUNNERS.find((nr) => nr.key === picksRef.current[0])?.name || "P1"} & ${NIGHT_RUNNERS.find((nr) => nr.key === picksRef.current[1])?.name || "P2"}`,
                supplies: humanRunners.reduce((sum, r) => sum + r.supplies, 0),
                escaped: humanRunners.some((r) => r.escaped),
              }
            : null;
        setNightLogged(null);
        setResults(board.map((r) => ({ name: r.name, human: r.human, color: r.color, character: r.character, supplies: r.supplies, escaped: r.escaped, rampages: r.rampages || 0, pts: payoutFor(r) })));
        setPayout(pts);
        phaseRef.current = "over";
        setPhase("over");
      }
    };

    onEventRef.current = onEvent;

    const onKey = (event) => {
      const coop = modeRef.current === "coop";
      const m1 = KEYMAP_P1[event.code];
      const m2 = KEYMAP_P2[event.code];
      const a1 = P1_ACTIONS[event.code];
      const a2 = P2_ACTIONS[event.code];
      let handled = false;
      if (coop) {
        if (m1) { keysRef.current.p1[m1] = true; handled = true; }
        if (m2) { keysRef.current.p2[m2] = true; handled = true; }
        if (a1 && event.code !== "Enter") { keysRef.current.p1[a1] = true; handled = true; } // Enter is P2's slash in co-op
        if (a2) { keysRef.current.p2[a2] = true; handled = true; }
      } else {
        // solo + online: every key drives this device's own runner (the guest's pad streams to the host)
        const target = onlineRef.current.role === "guest" ? sendPadRef.current : keysRef.current.p1;
        const mapped = m1 || m2;
        if (mapped) { target[mapped] = true; handled = true; }
        if (a1) { target[a1] = true; handled = true; }
      }
      if (handled) event.preventDefault();
      if (event.code === "Enter" && phaseRef.current !== "running" && onlineRef.current.role !== "guest") startNight();
    };
    const onKeyUp = (event) => {
      const m1 = KEYMAP_P1[event.code];
      const m2 = KEYMAP_P2[event.code];
      if (onlineRef.current.role === "guest") {
        const mapped = m1 || m2;
        if (mapped) sendPadRef.current[mapped] = false;
        return;
      }
      if (m1) keysRef.current.p1[m1] = false;
      if (m2) keysRef.current.p2[m2] = false;
    };

    // touch joystick — solo: drag anywhere steers P1. co-op: left half of the maze drives
    // P1, right half drives P2 (two thumbs, tracked independently by pointer id).
    // online guest: the single local thumb drives the pad streamed to the host
    const joys = { p1: null, p2: null };
    const padOf = (slot) => (onlineRef.current.role === "guest" && slot === "p1" ? sendPadRef.current : keysRef.current[slot]);
    const setJoyKeys = (pad, event, origin) => {
      const dx = event.clientX - origin.x;
      const dy = event.clientY - origin.y;
      const dead = 10;
      pad.left = dx < -dead;
      pad.right = dx > dead;
      pad.up = dy < -dead;
      pad.down = dy > dead;
    };
    const onPointerDown = (event) => {
      event.preventDefault();
      const coop = modeRef.current === "coop";
      const rect = canvas.getBoundingClientRect();
      const slot = coop && event.clientX >= rect.left + rect.width / 2 ? "p2" : "p1";
      if (joys[slot]) return;
      joys[slot] = { id: event.pointerId, x: event.clientX, y: event.clientY };
    };
    const onPointerMove = (event) => {
      for (const slot of ["p1", "p2"]) {
        if (joys[slot] && joys[slot].id === event.pointerId) setJoyKeys(padOf(slot), event, joys[slot]);
      }
    };
    const onPointerUp = (event) => {
      for (const slot of ["p1", "p2"]) {
        if (joys[slot] && joys[slot].id === event.pointerId) {
          joys[slot] = null;
          const pad = padOf(slot);
          pad.left = pad.right = pad.up = pad.down = false;
        }
      }
    };

    let raf = 0;
    const loop = () => {
      try {
        const g = stateRef.current;
        const role = onlineRef.current.role;
        if (phaseRef.current === "running" && role === "guest") {
          // guest device — no engine here. Stream our pad to the host, render their night.
          guestFrameRef.current += 1;
          if (guestFrameRef.current % 4 === 0 && netRef.current) {
            netRef.current.send({ type: "input", pad: { ...sendPadRef.current } });
            sendPadRef.current.use = false;
            sendPadRef.current.trap = false;
            sendPadRef.current.slash = false;
          }
          const a = snapARef.current;
          const b = snapBRef.current;
          if (b) {
            const span = a ? Math.max(1, b.t - a.t) : 1;
            const alpha = a ? Math.min(1, (performance.now() - a.t) / span) : 1;
            const renderG = lerpNight(a?.g, b.g, alpha);
            // the guest's HUD and runner ring treat P2 as "you" on this device
            renderG.frame = guestFrameRef.current;
            renderG.runners = renderG.runners.map((r) => ({ ...r, human: r.id === 1 }));
            renderNight(renderG, ctx, { sprites: spritesRef.current, staticRef, darkRef });
          }
        } else {
          if (g && phaseRef.current === "running" && !g.over) {
            stepNight(g, role === "host" ? { 0: keysRef.current.p1, 1: guestPadRef.current } : { 0: keysRef.current.p1, 1: keysRef.current.p2 }, { onEvent });
            // edge-triggered actions — one press, one fire
            for (const pad of [keysRef.current.p1, keysRef.current.p2]) {
              pad.use = false;
              pad.trap = false;
              pad.slash = false;
            }
            // host streams the night to the partner at ~20Hz
            if (role === "host" && netRef.current && g.frame % 3 === 0) {
              netRef.current.send({ type: "snap", g });
            }
          }
          if (g) renderNight(g, ctx, { sprites: spritesRef.current, staticRef, darkRef });
        }
      } catch (frameErr) {
        console.error("dolos night frame error", frameErr);
      }
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);

    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerUp);
    window.__dolosnight = { stateRef, phaseRef, keysRef, modeRef };

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKeyUp);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerUp);
      boardAlive = false;
      netRef.current?.close();
      netRef.current = null;
      delete window.__dolosnight;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- online co-op rooms (host-authoritative relay) ----------
  const leaveRoom = () => {
    netRef.current?.close();
    netRef.current = null;
    guestPadRef.current = emptyPad();
    guestPickRef.current = null;
    setOnline({ role: null, code: "", status: "idle" });
  };

  const hostRoom = async () => {
    try {
      const { data } = await axios.get(`${API}/night/room-code`);
      const code = data.code;
      const conn = connectNightRoom(code, "host", {
        onMessage: (msg) => {
          if (msg.type === "peer-joined") {
            setOnline({ role: "host", code, status: "ready" });
            toast.success("PARTNER CONNECTED — START THE NIGHT WHEN READY.");
          } else if (msg.type === "hello") {
            guestPickRef.current = msg.pick || "riff";
          } else if (msg.type === "input") {
            guestPadRef.current = { ...emptyPad(), ...msg.pad };
          } else if (msg.type === "peer-left") {
            guestPickRef.current = null;
            const g = stateRef.current;
            if (phaseRef.current === "running" && g && !g.over) {
              const p2 = g.runners[1];
              if (p2) p2.human = false; // the CPU drags their runner onward
              g.banner = "PARTNER SIGNAL LOST — THE CPU DRAGS THEIR RUNNER ONWARD";
              g.bannerTimer = 130;
            } else {
              setOnline({ role: "host", code, status: "waiting" });
            }
            toast.error("PARTNER LEFT THE ROOM.");
          }
        },
      });
      netRef.current = conn;
      setOnline({ role: "host", code, status: "waiting" });
    } catch {
      toast.error("ROOM BOARD UNREACHABLE — RETRY.");
    }
  };

  const joinRoom = (code) => {
    const clean = code.trim().toUpperCase();
    if (clean.length !== 4) return;
    const conn = connectNightRoom(clean, "guest", {
      onMessage: (msg) => {
        if (msg.type === "joined") {
          setOnline({ role: "guest", code: clean, status: "joined" });
          conn.send({ type: "hello", pick: picksRef.current[1] });
          toast.success(`INSIDE ROOM ${clean} — THE HOST STARTS THE NIGHT.`);
        } else if (msg.type === "no-room" || msg.type === "room-full") {
          toast.error(msg.type === "no-room" ? "NO ROOM UNDER THAT CODE." : "THAT ROOM IS FULL.");
          conn.close();
          if (netRef.current === conn) netRef.current = null;
          setOnline({ role: null, code: "", status: "idle" });
        } else if (msg.type === "start") {
          setPicks(msg.picks);
          picksRef.current = msg.picks;
          countRef.current = msg.total || 4;
          setRunnerCount(msg.total || 4);
          snapARef.current = null;
          snapBRef.current = null;
          stateRef.current = null;
          setResults(null);
          setPayout(0);
          setNightLogged(null);
          phaseRef.current = "running";
          setPhase("running");
        } else if (msg.type === "snap") {
          snapARef.current = snapBRef.current;
          snapBRef.current = { g: msg.g, t: performance.now() };
          stateRef.current = msg.g;
        } else if (msg.type === "event") {
          onEventRef.current?.(msg.kind);
        } else if (msg.type === "peer-left") {
          toast.error("HOST SIGNAL LOST — THE NIGHT IS OVER.");
          leaveRoom();
          phaseRef.current = "select";
          setPhase("select");
          stateRef.current = freshNight(1, 4, mulberry32(seedFromKey(dailyKey())));
        }
      },
    });
    netRef.current = conn;
  };

  const startNight = (total) => {
    const n = total || countRef.current;
    countRef.current = n;
    setRunnerCount(n);
    const coop = modeRef.current === "coop";
    const onlineHost = modeRef.current === "online" && onlineRef.current.role === "host";
    const humans = coop || onlineHost ? Math.min(2, n) : 1;
    const pickList = onlineHost
      ? [picksRef.current[0], guestPickRef.current || "riff"]
      : [picksRef.current[0], picksRef.current[1]];
    stateRef.current = freshNight(
      humans,
      Math.max(n, humans),
      mulberry32(seedFromKey(dailyKey())),
      pickList,
    );
    if (onlineHost) netRef.current?.send({ type: "start", picks: pickList, total: Math.max(n, humans) });
    keysRef.current = { p1: emptyPad(), p2: emptyPad() };
    guestPadRef.current = emptyPad();
    setResults(null);
    setPayout(0);
    setNightLogged(null);
    phaseRef.current = "running";
    setPhase("running");
  };

  // log the night's run on the shared weekly board — escapes outrank hauls
  const logNightScore = async () => {
    const name = nightName.trim();
    if (name.length < 2) return;
    setNightBusy(true);
    try {
      const duo = duoRunRef.current;
      const { data } = await axios.post(`${API}/dolosnight/score`, duo
        ? { name, supplies: duo.supplies, escaped: duo.escaped, tag: getLinkedTag(), coop: true, duo: duo.duo }
        : { name, supplies: myRunRef.current.supplies, escaped: myRunRef.current.escaped, tag: getLinkedTag() });
      setNightLogged(data);
      if (data.badge) toast.success(`BADGE FORGED — ${data.badge}. THE VAULT REMEMBERS.`);
      boardFetchRef.current?.();
    } catch {
      toast.error("SIGNAL LOST — TRY LOGGING AGAIN.");
    } finally {
      setNightBusy(false);
    }
  };

  const fireUse = () => { (onlineRef.current.role === "guest" ? sendPadRef.current : keysRef.current.p1).use = true; };
  const fireTrap = () => { (onlineRef.current.role === "guest" ? sendPadRef.current : keysRef.current.p1).trap = true; };
  const fireSlash = (pad = "p1") => { (onlineRef.current.role === "guest" ? sendPadRef.current : keysRef.current[pad]).slash = true; };

  return (
    <div className="dolosnight-game" data-testid="dolosnight-game">
      <div className={`game-frame ${phase !== "running" ? "is-select" : "is-running"}`}>
        <GameFullscreenButton testid="dolosnight-fullscreen" />
        <canvas ref={canvasRef} width={W} height={H} data-testid="dolosnight-canvas" aria-label="Dolo's Night maze survival game" />

        {phase === "select" ? (
          <div className="game-overlay" data-testid="dolosnight-select">
            <span><Moon size={13} /> SIDE QUEST // DOLO'S NIGHT — MAZE SURVIVAL</span>
            <h2>SURVIVE THE NIGHT.</h2>
            <p>
              A 2.5-minute night in a walled maze. Scavenge supplies, dodge the horde and the mutated dogs,
              hide in doorways, set traps — and be at a side door when they grind open before dawn.
            </p>
            <span className="dn-day-chip" data-testid="dolosnight-day">TONIGHT'S MAZE — {nightInfo?.day || dailyKey()} · SKY: {skinForDay().name} · SAME COURSE FOR EVERY RUNNER</span>
            <div className="dn-mode-picks" data-testid="dolosnight-mode-picks">
              <button type="button" className={`dn-mode-pick${mode === "solo" ? " is-active" : ""}`} onClick={() => setMode("solo")} data-testid="dolosnight-mode-solo">SOLO NIGHT</button>
              <button type="button" className={`dn-mode-pick${mode === "coop" ? " is-active" : ""}`} onClick={() => setMode("coop")} data-testid="dolosnight-mode-coop">CO-OP — SAME SCREEN</button>
              <button type="button" className={`dn-mode-pick${mode === "online" ? " is-active" : ""}`} onClick={() => setMode("online")} data-testid="dolosnight-mode-online">ONLINE CO-OP</button>
            </div>
            {mode === "online" ? (
              <div className="dn-runner-picks" data-testid="dolosnight-online-lobby">
                <div className="dn-runner-pick-block">
                  <span className="dn-count-label">YOUR RUNNER{online.role === "guest" ? " — GUEST (P2)" : " — HOST (P1)"}</span>
                  <div className="dn-runner-grid" data-testid="dolosnight-runner-grid-online">
                    {NIGHT_RUNNERS.map((nr) => {
                      const slot = online.role === "guest" ? 1 : 0;
                      return (
                        <button
                          key={nr.key}
                          type="button"
                          className={`dn-runner-card${picks[slot] === nr.key ? " is-active" : ""}`}
                          onClick={() => {
                            setPick(slot, nr.key);
                            if (online.role === "guest") netRef.current?.send({ type: "hello", pick: nr.key });
                          }}
                          data-testid={`dolosnight-pick-online-${nr.key}`}
                        >
                          <img src={nr.sprite || SPRITES.dolo.s[0]} alt={nr.name} loading="lazy" />
                          <strong>{nr.name}</strong>
                          <small>{nr.title}</small>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {!online.role ? (
                  <div className="dn-lobby-actions">
                    <button type="button" className="button button-primary" onClick={hostRoom} data-testid="dolosnight-host-room">HOST A ROOM</button>
                    <div className="dn-join-row">
                      <input
                        value={joinCode}
                        onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                        maxLength={4}
                        placeholder="ROOM CODE"
                        data-testid="dolosnight-join-code"
                      />
                      <button type="button" onClick={() => joinRoom(joinCode)} data-testid="dolosnight-join-room">JOIN ROOM</button>
                    </div>
                  </div>
                ) : online.role === "host" ? (
                  <div className="dn-room-status" data-testid="dolosnight-room-status">
                    <span>ROOM CODE — SEND IT TO YOUR PARTNER</span>
                    <strong className="dn-room-code" data-testid="dolosnight-room-code">{online.code}</strong>
                    <p>{online.status === "ready" ? "PARTNER CONNECTED — THE NIGHT WAITS FOR NO ONE" : "WAITING FOR YOUR PARTNER TO JOIN…"}</p>
                    {online.status === "ready" ? (
                      <button type="button" className="button button-primary" onClick={() => startNight()} data-testid="dolosnight-start-online"><Play size={15} /> ENTER THE MAZE TOGETHER</button>
                    ) : null}
                    <button type="button" className="game-resume" onClick={leaveRoom} data-testid="dolosnight-close-room">CLOSE ROOM</button>
                  </div>
                ) : (
                  <div className="dn-room-status" data-testid="dolosnight-room-status">
                    <span>ROOM {online.code}</span>
                    <p data-testid="dolosnight-guest-status">CONNECTED — THE HOST STARTS THE NIGHT. RUN YOUR LANE.</p>
                    <button type="button" className="game-resume" onClick={leaveRoom} data-testid="dolosnight-leave-room">LEAVE ROOM</button>
                  </div>
                )}
              </div>
            ) : (
            <div className={mode === "coop" ? "dn-runner-picks is-coop" : "dn-runner-picks"} data-testid="dolosnight-runner-picks">
              {(mode === "coop" ? [0, 1] : [0]).map((slot) => (
                <div className="dn-runner-pick-block" key={slot}>
                  <span className="dn-count-label">{mode === "coop" ? `P${slot + 1} — CHOOSE RUNNER` : "CHOOSE YOUR RUNNER"}</span>
                  <div className="dn-runner-grid" data-testid={`dolosnight-runner-grid-${slot}`}>
                    {NIGHT_RUNNERS.map((nr) => (
                      <button
                        key={nr.key}
                        type="button"
                        className={`dn-runner-card${picks[slot] === nr.key ? " is-active" : ""}`}
                        onClick={() => setPick(slot, nr.key)}
                        data-testid={`dolosnight-pick-${slot}-${nr.key}`}
                      >
                        <img src={nr.sprite || SPRITES.dolo.s[0]} alt={nr.name} loading="lazy" />
                        <strong>{nr.name}</strong>
                        <small>{nr.title}</small>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            )}
            <div className="dn-count-block">
              <span className="dn-count-label"><Users size={13} /> RUNNERS IN THE MAZE</span>
              <div className="dn-count-picks" data-testid="dolosnight-count-picks">
                {[2, 3, 4].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`dn-count-pick${runnerCount === n ? " is-active" : ""}`}
                    onClick={() => { setRunnerCount(n); countRef.current = n; }}
                    data-testid={`dolosnight-count-${n}`}
                  >
                    {n}
                    <small>{mode !== "solo" ? (n === 2 ? "P1 + P2" : n === 3 ? "2P + 1 CPU" : "2P + 2 CPU") : n === 2 ? "YOU + 1 CPU" : n === 3 ? "YOU + 2 CPU" : "YOU + 3 CPU"}</small>
                  </button>
                ))}
              </div>
            </div>
            <small>{mode === "coop"
              ? "P1 — WASD · SPACE SLASH · E ITEM · T TRAP  //  P2 — ARROWS · ENTER SLASH · R-SHIFT ITEM · R-CTRL TRAP  //  TOUCH: LEFT HALF = P1 · RIGHT HALF = P2"
              : mode === "online"
                ? "ONLINE — THE HOST'S DEVICE RUNS THE MAZE · BOTH: WASD / ARROWS · SPACE SLASH · E ITEM · T TRAP · TOUCH DRAG"
                : "MOVE WASD / ARROWS · SLASH SPACE · USE ITEM E · SET TRAP T · TOUCH: DRAG TO RUN"}</small>
            {mode !== "online" ? (
              <button type="button" className="button button-primary" onClick={() => startNight()} data-testid="dolosnight-start-button">
                <Play size={15} /> ENTER THE MAZE
              </button>
            ) : null}
          </div>
        ) : null}

        {phase === "over" && results ? (
          <div className="game-overlay" data-testid="dolosnight-over-screen">
            <span className="game-over-tag"><Sun size={14} /> DAWN — NIGHT SURVIVED</span>
            <h2 data-testid="dolosnight-payout">{payout > 0 ? `+${payout} PTS` : "NO PTS"}</h2>
            <p>{(online.role === "host" ? results.find((r) => r.name === "P1")?.escaped
              : online.role === "guest" ? results.find((r) => r.name === "P2")?.escaped
              : results.find((r) => r.human)?.escaped)
              ? "You made a side door before dawn. The wastes remember."
              : "You never reached a side door — but every supply you hauled counts."}</p>
            <div className="dn-results" data-testid="dolosnight-results">
              {results.map((r, i) => (
                <div key={`${r.name}-${i}`} className={`dn-result-row${r.human ? " is-you" : ""}`} data-testid={`dolosnight-result-${i}`}>
                  <span className="dn-result-place">#{i + 1}</span>
                  <img
                    className="dn-result-pose"
                    src={NIGHT_RUNNERS.find((nr) => nr.key === r.character)?.sprite || SPRITES.dolo.s[0]}
                    alt=""
                  />
                  <i className="dn-result-dot" style={{ background: r.color }} />
                  <strong>{
                    online.role === "host" ? (r.name === "P1" ? "P1 — YOU" : r.name)
                    : online.role === "guest" ? (r.name === "P2" ? "P2 — YOU" : r.name)
                    : r.human ? (mode === "coop" ? r.name : "YOU") : r.name
                  }</strong>
                  {r.character ? <span className="dn-runner-tag">{NIGHT_RUNNERS.find((nr) => nr.key === r.character)?.name}</span> : null}
                  <span>{r.supplies} SUPPLIES</span>
                  {r.rampages > 0 ? <span className="dn-rampage-bounty" data-testid={`dolosnight-rampage-${i}`}>+{r.rampages} RAMPAGE</span> : null}
                  <em className={r.escaped ? "dn-escaped" : "dn-lost"}>{r.escaped ? "ESCAPED" : "LEFT BEHIND"}</em>
                </div>
              ))}
            </div>
            <div className="daily-submit" data-testid="night-submit">
              <span>{online.role === "guest"
                ? "THE HOST LOGS YOUR DUO'S COMBINED HAUL"
                : online.role === "host" || mode === "coop"
                  ? "CO-OP BOARD — LOG YOUR DUO'S COMBINED HAUL"
                  : "WEEKLY NIGHT BOARD — LOG YOUR RUN"}</span>
              {nightLogged ? (
                <p className="daily-logged" data-testid="night-logged">
                  <Trophy size={13} /> LOGGED — RANK #{nightLogged.rank} THIS WEEK
                  {nightLogged.streak > 0 ? ` · ESCAPE STREAK ×${nightLogged.streak}` : ""}
                  {nightLogged.badge ? " · DAWN WALKER" : ""}
                </p>
              ) : online.role === "guest" ? (
                <p className="daily-logged" data-testid="night-guest-note">YOUR HAUL BANKED ON THIS DEVICE — THE HOST'S CALLSIGN CARRIES THE DUO.</p>
              ) : (
                <div className="daily-submit-row">
                  <input
                    value={nightName}
                    onChange={(e) => setNightName(e.target.value)}
                    maxLength={24}
                    placeholder="CALLSIGN"
                    data-testid="night-callsign-input"
                  />
                  <button type="button" className="button button-primary" onClick={logNightScore} disabled={nightBusy} data-testid="night-log-button">
                    {nightBusy ? "LOGGING…" : "LOG NIGHT"}
                  </button>
                </div>
              )}
            </div>
            <small><Trophy size={11} /> 1 PT PER 3 SUPPLIES · +5 FOR ESCAPING · BANKED TO YOUR STASH</small>
            <button type="button" className="button button-primary" onClick={() => startNight()} data-testid="dolosnight-rematch-button">
              <Play size={15} /> RUN IT BACK
            </button>
            <button type="button" className="game-resume" onClick={() => { phaseRef.current = "select"; setPhase("select"); stateRef.current = freshNight(1, 4, mulberry32(seedFromKey(dailyKey()))); }} data-testid="dolosnight-change-count-button">
              <RotateCcw size={13} /> CHANGE RUNNERS
            </button>
          </div>
        ) : null}
      </div>

      <div className="dn-action-bar" data-testid="dolosnight-action-bar">
        <button type="button" className="dn-slash-btn" onClick={() => fireSlash("p1")} disabled={phase !== "running"} data-testid="dolosnight-slash-button">{mode === "coop" ? "P1 SLASH [SPACE]" : "SLASH [SPACE]"}</button>
        {mode === "coop" ? (
          <button type="button" className="dn-slash-btn is-p2" onClick={() => fireSlash("p2")} disabled={phase !== "running"} data-testid="dolosnight-slash-button-p2">P2 SLASH [ENTER]</button>
        ) : null}
        <button type="button" onClick={fireUse} disabled={phase !== "running"} data-testid="dolosnight-use-button">{mode === "coop" ? "P1 USE ITEM [E]" : "USE ITEM [E]"}</button>
        <button type="button" onClick={fireTrap} disabled={phase !== "running"} data-testid="dolosnight-trap-button">{mode === "coop" ? "P1 SET TRAP [T]" : "SET TRAP [T]"}</button>
        <span className="dn-action-hint">{mode === "coop" ? "TOUCH: LEFT HALF OF THE MAZE DRIVES P1 · RIGHT HALF DRIVES P2" : online.role ? "ONLINE CO-OP — YOUR MOVES FLY THROUGH THE RELAY" : "DRAG ON THE MAZE TO RUN ON TOUCH"}</span>
      </div>

      <div className="daily-challenge" data-testid="night-board">
        <div className="daily-challenge-head">
          <span className="eyebrow">NIGHT BOARD // WEEKLY HUNT</span>
          <strong data-testid="night-board-week">{nightInfo ? `WEEK ${nightInfo.week.split("-W")[1]} — TONIGHT'S MAZE ${nightInfo.day}` : "THIS WEEK'S HUNT"}</strong>
          {nightInfo?.champion ? (
            <span className="daily-yesterday" data-testid="night-champion">
              <Trophy size={12} /> LAST WEEK'S WALKER — {nightInfo.champion.name} · {nightInfo.champion.escapes} {nightInfo.champion.escapes === 1 ? "ESCAPE" : "ESCAPES"}
            </span>
          ) : null}
          {nightInfo?.coop_champion ? (
            <span className="daily-yesterday is-duo" data-testid="night-coop-champion">
              <Users size={12} /> LAST WEEK'S TOP DUO — {nightInfo.coop_champion.name}{nightInfo.coop_champion.duo ? ` (${nightInfo.coop_champion.duo})` : ""} · {nightInfo.coop_champion.escapes} ESC
            </span>
          ) : null}
          <p>One maze per UTC day — same walls, same drops, same horde for every runner. Escapes outrank hauls; the board resets each Monday.</p>
          <span className="dn-tomorrow-sky" data-testid="night-board-tomorrow">
            TOMORROW'S SKY — {skinForDay(new Date(Date.now() + 86400000)).name} · NEW MAZE AT 00:00 UTC
          </span>
          <Link to="/champions" className="champions-play-link" data-testid="night-board-hall-link">CHAMPIONS HALL →</Link>
        </div>
        <div className="daily-board" data-testid="night-board-rows">
          {nightInfo?.rows?.length ? (
            nightInfo.rows.slice(0, 5).map((row, i) => (
              <div className={`daily-board-row${i === 0 ? " is-leader" : ""}`} key={row.name} data-testid={`night-board-row-${i + 1}`}>
                <span>{["1ST", "2ND", "3RD", "4TH", "5TH"][i]}</span>
                <strong>{row.name}</strong>
                <em>{row.escapes} ESC · BEST {row.best_supplies} · {row.nights} {row.nights === 1 ? "NIGHT" : "NIGHTS"}</em>
              </div>
            ))
          ) : (
            <span className="daily-board-empty">NO RUNS LOGGED THIS WEEK — THE NIGHT IS UNCLAIMED</span>
          )}
        </div>
        <div className="daily-coop" data-testid="night-coop-board">
          <span className="daily-coop-label"><Users size={11} /> CO-OP DUOS — COMBINED HAULS</span>
          {nightInfo?.coop_rows?.length ? (
            nightInfo.coop_rows.slice(0, 3).map((row, i) => (
              <div className={`daily-board-row is-coop${i === 0 ? " is-leader" : ""}`} key={row.name} data-testid={`night-coop-row-${i + 1}`}>
                <span>{["1ST", "2ND", "3RD"][i]}</span>
                <strong>{row.name}{row.duo ? <em className="duo-tag">{row.duo}</em> : null}</strong>
                <em>{row.escapes} ESC · BEST {row.best_supplies} · {row.nights} {row.nights === 1 ? "NIGHT" : "NIGHTS"}</em>
              </div>
            ))
          ) : (
            <span className="daily-board-empty">NO DUO RUNS YET — GRAB A FRIEND, ONE SCREEN, ONE NIGHT</span>
          )}
        </div>
      </div>

      <div className="game-howto" data-testid="dolosnight-howto">
        <button
          type="button"
          className={`game-howto-toggle ${howtoOpen ? "is-open" : ""}`}
          onClick={() => setHowtoOpen((v) => !v)}
          data-testid="dolosnight-howto-toggle"
        >
          HOW TO PLAY // NIGHT RULES
          <ChevronDown size={14} />
        </button>
        {howtoOpen ? (
          <div className="game-howto-grid" data-testid="dolosnight-howto-panel">
            {[
              ["THE NIGHT", "2.5 minutes of darkness in a tall maze. Scavenge cans, medkits and lore for supplies — the left and right side doors grind open for the final 30 seconds. Pick one and run."],
              ["SWORD SLASH", "SPACE / K or the SLASH button — the red blade glows, auto-turns to the nearest zombie or dog in reach, and one slash ELIMINATES a zombie for good (dogs are only stunned). Quick cooldown."],
              ["THE HORDE", "Zombies hunt by scent and more rise as the night wears on. Mutated dogs hold lanes and charge anything that crosses them."],
              ["HIDE", "Stand in a doorway or vent and the horde loses your scent. Smoke canisters hide you even on the move."],
              ["SANCTUARY", "The lit SAFE pad at the maze heart — zombies and dogs cannot cross its threshold. Breathe, watch them crowd the light, then run."],
              ["TRAPS", "Press T to arm a scrap trap — zombies drop through them, dogs come out snarling. Crates restock your charges."],
              ["POWER ITEMS", "One slot. Press E to fire: flare repels the horde, adrenaline sprints, the boombox lure drags them away and blows, scrap armor blocks one takedown."],
              ["RIVALS", "Up to three CPU runners share your maze. They grab the same supplies you need — and their spilled loot is fair game."],
              ["CO-OP — SAME SCREEN", "Two runners, one night, one device. P1: WASD + SPACE slash + E item + T trap. P2: arrow keys + ENTER slash + RIGHT-SHIFT item + RIGHT-CTRL trap. On touch, the left half of the maze drives P1 and the right half drives P2. Escape together or alone — each runner banks their own dawn payout."],
              ["ONLINE CO-OP", "Two devices, one night. The host opens a room and shares the 4-letter code; the partner joins from theirs. The host's device runs the maze and streams it to the guest in real time — if either signal drops, the CPU drags that runner onward. Each device banks its own runner's payout; the host logs the duo's combined haul."],
              ["GETTING DOWNED", "The horde drags you back to your corner and spills 40% of your supplies. Scrap armor eats one hit instead."],
              ["PAYOUT", "1 PT per 3 supplies, +5 PTS for escaping through a side door, +1 PT for every rampage kill. Banked straight to your rewards stash."],
              ["SHARED MAZE", "The walls regenerate from the daily seed every UTC midnight — every runner worldwide faces the exact same maze, drops, horde, and sky that day. The palette rotates nightly: ash static, toxic green CRT, blood-red dusk."],
              ["NIGHT BOARD", "Log your run after dawn: escapes outrank supply hauls on the weekly board, which resets each Monday. Sunday 18:05 UTC the board leader is crowned — +50 PTS to their linked Titan Tag."],
              ["DAWN WALKER", "Escape the maze three nights running (consecutive days) with a linked Titan Tag and a permanent DAWN WALKER badge is forged onto your vault."],
            ].map(([term, desc]) => (
              <div key={term} className="game-howto-row">
                <strong>{term}</strong>
                <span>{desc}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default DolosNight;
